<script setup lang="ts">
import { PAY_ONLINE_24_DISABLED } from "~/config/pay-online-24";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { isPossiblePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";

const { t } = useI18n({ useScope: "local" });
const localePath = useLocalePath();
const route = useRoute();
const payOnline24Url = useRuntimeConfig().public.payOnline24Url;
const { $post } = useAxios();

function phoneValidator(value: string) {
  return isPossiblePhoneNumber(value, "RU") && isValidPhoneNumber(value, "RU");
}

const rules = computed(() => ({
  phone: {
    required,
    phone: phoneValidator,
  },
}));

const phone = ref("");
const isLoading = ref(false);

const v$ = useVuelidate(rules, { phone });

const formatedPhoneNumber = computed({
  get() {
    if (!phone.value) return "";

    const s = "+7 " + phone.value;
    const x = s
      .replace(/\D/g, "")
      .match(/(\d{0,1})(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})/);

    if (x) x[1] = "+7";

    if (x)
      return (
        !x[3]
          ? x[1] + " (" + x[2]
          : x[1] +
            " (" +
            x[2] +
            ") " +
            x[3] +
            (x[4] ? " " + x[4] : "") +
            (x[5] ? " " + x[5] : "")
      )
        .split("+7")[1]
        .trim();
    else return "";
  },
  set(value: string) {
    phone.value = value.replace(/\D/g, "");
  },
});

if (!PAY_ONLINE_24_DISABLED) {
  v$.value.$touch();
}

async function onSubmit() {
  try {
    v$.value.$touch();
    if (v$.value.phone.$error) return;
    isLoading.value = true;

    const checkRequest = await $post(
      `${payOnline24Url}/api/pay-online-24/payments/check-request`,
      { phoneNumber: phone.value },
    );
    localStorage.setItem(
      "pay-online-24-session-token",
      checkRequest.sessionToken,
    );

    window.location.href = `${route.fullPath}/${checkRequest.id}?maskAcc=${checkRequest.maskAcc}&maskFio=${checkRequest.maskFio}&comissionCard=${checkRequest.commissions.CARD}&comissionQR=${checkRequest.commissions.QR}`;
  } catch (e) {
    console.error(e);
    if (e) {
      alert("Такой пользователь не найден");
    }
  } finally {
    isLoading.value = false;
  }
}

function onlyNumbers(event: KeyboardEvent) {
  const key = event.key;
  if (!/[0-9]/.test(key)) {
    event.preventDefault();
  }
  const input = event.target as HTMLInputElement;
  const value = input.value.replace(/\D/g, "");
  if (value.length >= 10 && /[0-9]/.test(key)) {
    event.preventDefault();
  }
}
</script>

<template>
  <nuxt-layout name="default">
    <main id="main" class="main">
      <div class="container relative">
        <!-- Заглушка: PAY_ONLINE_24_DISABLED = true -->
        <div
          v-if="PAY_ONLINE_24_DISABLED"
          class="min-h-screen lg:min-h-100 flex flex-col space-y-6 p-10 mx-auto justify-center text-center max-w-2xl"
        >
          <p class="text-xl lg:text-3xl font-firasans">
            {{ t("title") }}
          </p>
          <p class="text-sm lg:text-base text-[#555] font-sans">
            {{ t("description") }}
          </p>

          <nuxt-link :to="localePath('/')">
            <a-button
              class="min-w-60 max-w-60 text-center mx-auto mt-4"
              type="yellow"
            >
              {{ t("back_to_main") }}
            </a-button>
          </nuxt-link>
        </div>

        <!-- Оригинальная форма: PAY_ONLINE_24_DISABLED = false -->
        <template v-else>
          <div
            class="intro-text text-center text-xl lg:text-3xl font-firasans mx-auto mt-10"
          >
            <p>
              {{ t("about") }}
            </p>
          </div>
          <div
            class="max-w-100 lg:min-h-120 min-h-110 flex flex-col space-y-3 lg:space-y-5 p-10 mx-auto justify-center"
          >
            <div class="instruction-text font-sans">
              <p class="text-sm lg:text-base">
                {{ t("helper") }}
              </p>
            </div>
            <a-input
              ref="phoneInput"
              v-model="formatedPhoneNumber"
              :max="10"
              type="tel"
              class="min-w-full rounded-2xl"
              :class="[!v$.phone.$error ? 'error' : '']"
              :placeholder="t('phone_number')"
              outlined
              filled
              @keypress="onlyNumbers"
              @keyup.enter="onSubmit"
            >
              <template v-slot:prepend> +7 </template>
            </a-input>

            <a-button
              class="min-w-full text-center rounded-2xl"
              type="yellow"
              :disabled="v$.phone.$error || isLoading"
              @click="onSubmit"
            >
              {{ isLoading ? "Загрузка..." : t("enter") }}
            </a-button>
          </div>
        </template>
      </div>
    </main>
  </nuxt-layout>
</template>

<style scoped>
.yellow-btn:disabled {
  background: #dcdcdc !important;
  color: #b7b7b7;
  margin-bottom: 28px;
}

.yellow-btn {
  margin-bottom: 0;
}

.form__btn {
  margin-top: 28px;
  margin-bottom: 28px;
  max-width: 207px !important;
}

.instruction-text {
  color: #555;
}
</style>

<i18n lang="json">
{
  "ru": {
    "title": "Мы работаем над улучшением услуги",
    "description": "Сервис пополнения счёта и карты временно недоступен. Скоро он снова появится в обновлённом виде. Приносим извинения за неудобства.",
    "back_to_main": "Вернуться на главную",
    "phone_number": "Номер телефона",
    "enter": "Далее",
    "about": "Услуга позволяет пополнить счёт или карту Амра-Банк с карты любого российского банка.",
    "helper": "Укажите номер телефона, привязанный к счёту или карте клиента Амра-Банк."
  },
  "en": {
    "title": "We are improving this service",
    "description": "The account and card top-up service is temporarily unavailable. It will be back soon in an improved version. We apologize for the inconvenience.",
    "back_to_main": "Back to main page",
    "phone_number": "Phone number",
    "enter": "Next",
    "about": "The service allows you to top up your Amra-Bank account or card using a card from any Russian bank.",
    "helper": "Enter the phone number linked to the Amra-Bank account or card."
  },
  "es": {
    "title": "Estamos mejorando este servicio",
    "description": "El servicio de recarga de cuenta y tarjeta no está disponible temporalmente. Pronto volverá en una versión mejorada. Disculpe las molestias.",
    "back_to_main": "Volver a la página principal",
    "phone_number": "Número de teléfono",
    "enter": "Siguiente",
    "about": "El servicio permite recargar su cuenta o tarjeta de Amra-Bank con una tarjeta de cualquier banco ruso.",
    "helper": "Indique el número de teléfono vinculado a la cuenta o tarjeta de Amra-Bank."
  },
  "tr": {
    "title": "Bu hizmeti geliştiriyoruz",
    "description": "Hesap ve kart yükleme hizmeti geçici olarak kullanılamıyor. Yakında geliştirilmiş haliyle yeniden açılacak. Anlayışınız için teşekkür ederiz.",
    "back_to_main": "Ana sayfaya dön",
    "phone_number": "Telefon numarası",
    "enter": "İleri",
    "about": "Bu hizmet, herhangi bir Rus bankasına ait bir kartla Amra-Bank hesabınızı veya kartınızı doldurmanızı sağlar.",
    "helper": "Amra-Bank hesabınıza veya kartınıza bağlı telefon numarasını girin."
  },
  "ar": {
    "title": "نعمل على تحسين هذه الخدمة",
    "description": "خدمة تعبئة الحساب والبطاقة غير متاحة مؤقتًا. ستعود قريبًا بشكل محسّن. نعتذر عن الإزعاج.",
    "back_to_main": "العودة إلى الصفحة الرئيسية",
    "phone_number": "رقم الهاتف",
    "enter": "التالي",
    "about": "تتيح الخدمة تعبئة حساب أو بطاقة أمرا بنك باستخدام بطاقة من أي بنك روسي.",
    "helper": "أدخل رقم الهاتف المرتبط بحساب أو بطاقة أمرا بنك."
  },
  "ab": {
    "title": "Ауслуга ахәыӡәаратәуп",
    "description": "Ахәаҧшра ауслуга аҭагылазаашьа аҿы иаҭахуп. Уажәы уи ахәыӡәатәуп. Аҭагылазаашьа рыла иҭабуп.",
    "back_to_main": "Ахадаҩы аҭалара",
    "phone_number": "Аҭел аномер",
    "enter": "Аиасра",
    "about": "Ауслуга амра-банк ахәаҧшра ала ахарҭа рыла иаҭаххоит.",
    "helper": "Амра-банк ахәаҧшра ала ахарҭа рыла иаҿкатәуп аҭел аномер иҭаҵаагы."
  }
}
</i18n>
